// Цели (вкладка «Телоскопа»): активные с прогрессом, форма новой цели, архив достигнутых.

import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Input,
  Modal,
  PageSpinner,
  Select,
  useToast,
} from '@/components/ui'
import { formatDate, parseDecimal } from '@/lib/format'
import { useExercises } from '@/pages/workouts/api'

import { goalsApi, useGoalMutation, useGoals, type Goal, type GoalKind } from './api'
import { KIND_OPTIONS, initialGoalForm, type GoalPrefill } from './logic'
import { GoalBar } from './parts'

const FORM_FIELDS = ['exercise_id', 'target_value', 'start_value', 'target_date']

function fieldError(error: unknown, field: string): string | null {
  return error instanceof ApiError && error.field === field ? error.message : null
}

function formError(error: unknown): unknown {
  return error instanceof ApiError && error.field && FORM_FIELDS.includes(error.field) ? null : error
}

function NewGoalDialog({ onClose, prefill }: { onClose: () => void; prefill?: GoalPrefill }) {
  const toast = useToast()
  const [kind, setKind] = useState<GoalKind>(prefill?.kind ?? 'weight')
  const [target, setTarget] = useState(prefill?.target ?? '')
  const [start, setStart] = useState(prefill?.start ?? '')
  const [date, setDate] = useState('')
  const [exerciseId, setExerciseId] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const exercises = useExercises()
  const create = useGoalMutation(goalsApi.create)
  const unit = KIND_OPTIONS.find((o) => o.value === kind)?.unit

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const targetValue = parseDecimal(target)
    if (targetValue === null) {
      setLocalError('Введи целевое значение')
      return
    }
    setLocalError(null)
    create.mutate(
      {
        kind,
        target_value: targetValue,
        start_value: parseDecimal(start),
        target_date: date || null,
        exercise_id: kind === 'exercise_weight' ? exerciseId || null : null,
      },
      {
        onSuccess: () => {
          toast.show({ message: 'Цель добавлена', tone: 'accent' })
          onClose()
        },
      },
    )
  }

  return (
    <Modal open onClose={onClose} title="Новая цель">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Select
          label="Что отслеживать"
          value={kind}
          onChange={(e) => setKind(e.target.value as GoalKind)}
          options={KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
        {kind === 'exercise_weight' ? (
          <Select
            label="Упражнение"
            value={exerciseId}
            onChange={(e) => setExerciseId(e.target.value)}
            placeholder="Выбери упражнение"
            options={(exercises.data ?? []).map((x) => ({ value: x.id, label: x.name }))}
            error={fieldError(create.error, 'exercise_id')}
          />
        ) : null}
        <Input
          label="Цель"
          inputMode="decimal"
          suffix={unit}
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          error={localError ?? fieldError(create.error, 'target_value')}
        />
        <Input
          label="Старт"
          hint="Пусто — возьмём текущее значение"
          inputMode="decimal"
          suffix={unit}
          value={start}
          onChange={(e) => setStart(e.target.value)}
          error={fieldError(create.error, 'start_value')}
        />
        <Input
          label="К дате (необязательно)"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          error={fieldError(create.error, 'target_date')}
        />
        <ErrorText error={formError(create.error)} />
        <Button type="submit" variant="primary" loading={create.isPending}>
          Добавить цель
        </Button>
      </form>
    </Modal>
  )
}

function ActiveGoal({ goal }: { goal: Goal }) {
  const toast = useToast()
  const status = useGoalMutation((s: 'achieved' | 'archived') => goalsApi.setStatus(goal.id, s))
  const undo = useGoalMutation(goalsApi.undo)
  const remove = useGoalMutation(() => goalsApi.remove(goal.id))
  return (
    <li className="py-3">
      <GoalBar goal={goal} />
      <div className="mt-2 flex flex-wrap gap-2">
        {goal.progress.reached ? (
          <Button size="sm" variant="primary" onClick={() => status.mutate('achieved')} loading={status.isPending}>
            Отметить достигнутой
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={() => status.mutate('archived')}>
          В архив
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            remove.mutate(undefined, {
              onSuccess: ({ change_id }) =>
                toast.show({
                  message: 'Цель удалена',
                  action: { label: 'Отменить', onClick: () => undo.mutate(change_id) },
                  duration: 8000,
                }),
            })
          }
        >
          Удалить
        </Button>
      </div>
    </li>
  )
}

export function GoalsPage() {
  // Вкладка «Состав тела» открывает форму с подставленными значениями: /teloscope/goals?new=1&kind=…&target=…
  const [params, setParams] = useSearchParams()
  const [prefill] = useState(() => initialGoalForm(params))
  const [creating, setCreating] = useState(prefill !== null)
  const active = useGoals('active')
  const closed = useGoals('closed')
  return (
    <>
      <Card
        title="Активные"
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            Новая цель
          </Button>
        }
      >
        {active.isLoading ? (
          <PageSpinner />
        ) : active.error ? (
          <ErrorText error={active.error} />
        ) : (active.data ?? []).length === 0 ? (
          <EmptyState
            icon="target"
            title="Целей пока нет"
            description="Вес к дате, шаги в день, сон, тренировки в неделю или вес в упражнении."
            action={<Button onClick={() => setCreating(true)}>Задать цель</Button>}
          />
        ) : (
          <ul className="divide-y divide-border">
            {(active.data ?? []).map((g) => (
              <ActiveGoal key={g.id} goal={g} />
            ))}
          </ul>
        )}
      </Card>
      {(closed.data ?? []).length > 0 ? (
        <Card title="Архив" className="mt-4">
          <ul className="divide-y divide-border">
            {(closed.data ?? []).map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="min-w-0 truncate text-fg">{g.title}</span>
                <span className="shrink-0 text-xs text-muted">
                  {g.status === 'achieved' ? `достигнута ${formatDate(g.achieved_at)}` : 'в архиве'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      {creating ? (
        <NewGoalDialog
          prefill={prefill ?? undefined}
          onClose={() => {
            setCreating(false)
            if (params.has('new')) setParams({}, { replace: true })
          }}
        />
      ) : null}
    </>
  )
}
